/* eslint-disable react/require-default-props */
import {
  forwardRef,
  useState,
  useCallback,
  useRef,
  memo,
  useMemo,
  useEffect
} from 'react'
import DesktopDatePicker, {
  DesktopDatePickerProps
} from '@mui/lab/DesktopDatePicker'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import { useTheme } from '@mui/material/styles'
import { InputLabel, useMediaQuery, Theme } from '@mui/material'
import localeDe from 'date-fns/locale/de'
import Tooltip from 'components/Tooltip'
import TooltipButton from 'components/Tooltip/Button'
import { isTenantSmava } from 'utils/url'
import { scrollTo } from 'utils/pollard/scroll'
import isNotAffiliate from 'utils/formDependencies/conditions/isNotAffiliate'
import { isInIframeContext } from 'stores/RootStore'
import { useStores } from 'stores/utils/hooks/useStores'
import { useUrlParams } from 'stores/utils/hooks/useUrlParams'
import { convertFormat } from 'components/PathDateInput/helpers'
import useEmotionStyles from 'hooks/useEmotionStyles'
import * as CSSTypes from 'csstype'
import Markdown from 'components/Markdown'

interface OwnProps {
  outerLabel?: boolean
  onChange?: DesktopDatePickerProps<any>['onChange']
  onBlur?: (date: unknown, raw?: string | null) => void
  onFocus?: (date: unknown, raw?: string | null) => void
  value?: string | DesktopDatePickerProps<any>['value']
  onTooltip?: (isDisplayed: boolean) => void
  tooltip?: string
  showTooltip?: boolean
  error?: React.ReactNode
  path?: string
  smallTextField?: boolean
  showTooltipOnFieldClick?: boolean
  isTooltipHidden?: boolean
  format: string
  name?: string
  placeholder?: string
}

type DatePickerProps = Omit<
  DesktopDatePickerProps<any>,
  | 'helperText'
  | 'onChange'
  | 'onBlur'
  | 'onFocus'
  | 'value'
  | 'inputValue'
  | 'open'
  | 'error'
  | 'TextFieldComponent'
  | 'renderInput'
> &
  OwnProps

const styles = (theme: Theme) => ({
  textFieldContainer: {
    display: 'flex',
    justifyContent: 'center'
  },
  container: {
    display: 'flex',
    flexDirection: 'column' as CSSTypes.Property.FlexDirection,
    position: 'relative' as CSSTypes.Property.Position
  },
  labelStyle: {
    fontSize: '14px',
    color: theme.palette.grey[900],
    marginBottom: '8px'
  },
  root: {
    margin: 0,
    '& .Mui-error': {
      padding: '0'
    }
  }
})

export const PATHS_WITH_YEAR_AUTOCOMPLETION = [
  'debtors.primary.personal.birthday',
  'debtors.secondary.personal.birthday'
]

const DatePicker = forwardRef<HTMLDivElement | null, DatePickerProps>(
  (
    {
      value = null,
      onChange,
      onBlur,
      onFocus,
      tooltip,
      onTooltip,
      showTooltip = false,
      error,
      label,
      path,
      outerLabel,
      smallTextField,
      showTooltipOnFieldClick,
      isTooltipHidden,
      format,
      name,
      placeholder
    }: DatePickerProps,
    ref
  ) => {
    const {
      navigation: { currentPageInSession }
    } = useStores()
    const urlParams = useUrlParams()
    const { textFieldContainer, container, labelStyle, root } =
      useEmotionStyles(styles)
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))
    const textRef = useRef<HTMLDivElement | null>(null)
    const labelText =
      textRef.current?.getElementsByTagName('label')?.[0]?.innerText

    const [shouldShrink, setShrink] = useState<boolean | undefined>(undefined)
    const [showHint, setHint] = useState<boolean>(false)

    const isSmava = isTenantSmava()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    const [selectedDate, setSelectedDate] = useState(
      typeof value === 'string' && value.length === 0 ? null : value
    )
    const [text, setText] = useState(
      typeof value === 'string' ? value : String(value) || ''
    )

    const handleChange = useCallback(
      (date: unknown, raw?: string | null) => {
        setSelectedDate(date)
        setText(raw || '')

        setShrink(true)

        if (onChange) {
          onChange(date, raw || undefined)
        }
      },
      [onChange]
    )

    const handleBlur = useCallback(() => {
      setShrink(
        Boolean(selectedDate) ||
          Boolean(isSmallScreen && labelText && labelText.length > 10)
      )

      setTimeout(() => {
        setHint(false)
      }, 300)

      if (!onBlur) {
        return
      }

      if (path && PATHS_WITH_YEAR_AUTOCOMPLETION.includes(path) && format) {
        setText(convertFormat(text, format, format, true))
      }

      onBlur(selectedDate, text)
    }, [onBlur, selectedDate, text, isSmallScreen, labelText, format, path])

    useEffect(() => {
      if (isSmallScreen && labelText && labelText.length > 10) {
        setShrink(true)
      }
    }, [isSmallScreen, labelText])

    const handleFocus = useCallback(() => {
      setShrink(true)
      if (!onFocus) {
        return
      }

      onFocus(selectedDate, text)

      if (
        textRef.current &&
        isSmava &&
        currentPageInSession === 'contact' &&
        isMobile &&
        isNotAffiliate(urlParams?.context)
      ) {
        const scrollTop =
          window.scrollY ||
          window.pageYOffset ||
          document.body.scrollTop +
            ((document.documentElement && document.documentElement.scrollTop) ||
              0)

        const pos = textRef.current.getBoundingClientRect().top + scrollTop
        scrollTo(pos - 200, isInIframeContext())
      }

      setTimeout(() => {
        setHint(Boolean(showTooltipOnFieldClick))
      }, 300)
    }, [
      onFocus,
      selectedDate,
      text,
      isMobile,
      currentPageInSession,
      showTooltipOnFieldClick,
      isSmava,
      urlParams?.context
    ])

    const [isTooltipDisplayed, setTooltipDisplayed] = useState(showTooltip)
    const tooltipDisplayRef = useRef(isTooltipDisplayed)
    const toggleTooltip = useCallback(() => {
      tooltipDisplayRef.current = !tooltipDisplayRef.current
      setTooltipDisplayed(tooltipDisplayRef.current)

      if (onTooltip) {
        onTooltip(tooltipDisplayRef.current)
      }
    }, [onTooltip])

    const [isOpen, setOpen] = useState(false)
    const openPicker = useCallback(() => setOpen(true), [setOpen])
    const closePicker = useCallback(() => setOpen(false), [setOpen])

    const inputLabelProps = useMemo(
      () => ({
        shrink: shouldShrink
      }),
      [shouldShrink]
    )

    const inputId =
      label && `text-input-${label.toString().toLowerCase().replace(/\s/g, '')}`

    const renderTextField = useCallback(
      ({ inputProps, ...textFieldProps }: TextFieldProps) => (
        <div className={container}>
          <div className={textFieldContainer}>
            <TextField
              name={name}
              size={smallTextField ? 'small' : undefined}
              id={inputId || ''}
              ref={textRef}
              {...textFieldProps}
              error={!!error}
              type="tel"
              inputMode="numeric"
              variant="outlined"
              InputProps={{ endAdornment: null }}
              className={root}
              label={(!outerLabel && label) || ''}
              helperText={
                typeof error === 'string' ? <Markdown>{error}</Markdown> : error
              }
              onBlur={handleBlur}
              onFocus={handleFocus}
              fullWidth
              InputLabelProps={inputLabelProps}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              inputProps={{
                ...inputProps,
                // CAT-2230 for some weird reason, inputprops.value is arriving with day and month inverted. this fixes the display
                value:
                  text.length === format.length
                    ? text
                    : inputProps?.value || text,
                placeholder
              }}
            />
            {tooltip && !showTooltipOnFieldClick && !isTooltipHidden && (
              <TooltipButton
                onClick={toggleTooltip}
                path={path}
                isVisible={isTooltipDisplayed}
              />
            )}
          </div>
        </div>
      ),
      [
        container,
        textFieldContainer,
        inputId,
        root,
        tooltip,
        toggleTooltip,
        smallTextField,
        showTooltipOnFieldClick,
        isTooltipHidden,
        outerLabel,
        label,
        error,
        handleBlur,
        handleFocus,
        inputLabelProps,
        placeholder,
        name,
        text,
        path,
        isTooltipDisplayed
      ]
    )

    const maskBasedOnFormat = format.replace(/[A-Za-z]/g, '_')

    return (
      <>
        {outerLabel && (
          <InputLabel htmlFor={inputId || ''} classes={{ root: labelStyle }}>
            {label}
          </InputLabel>
        )}
        {tooltip && !isTooltipHidden && (
          <Tooltip
            open={showHint}
            isAvailable={showTooltipOnFieldClick}
            isMinimalistic={showTooltipOnFieldClick}
          >
            {tooltip}
          </Tooltip>
        )}
        <LocalizationProvider dateAdapter={AdapterDateFns} locale={localeDe}>
          <DesktopDatePicker
            inputFormat={format}
            mask={maskBasedOnFormat}
            value={selectedDate}
            onChange={handleChange}
            renderInput={renderTextField}
            ref={ref}
            open={isOpen}
            onOpen={openPicker}
            onClose={closePicker}
          />
        </LocalizationProvider>
        {tooltip && !isTooltipHidden && !showTooltipOnFieldClick && (
          <Tooltip open={isTooltipDisplayed}>{tooltip}</Tooltip>
        )}
      </>
    )
  }
)

DatePicker.displayName = 'DatePicker'

export default memo(
  DatePicker,
  (prevProps, nextProps) =>
    JSON.stringify(prevProps) === JSON.stringify(nextProps)
)
